import type { Block, Text } from '@ksa/model'
import { SiteProse, SiteText, hasText, useRender } from '../context.tsx'

type Item = { quote: Text; name: Text; rating: number }
type Props = { heading: Text; items: Item[] }

/** Stars, drawn rather than typed. An emoji ★ renders as a different glyph on every platform and is
 *  announced letter by letter; a number in an `aria-label` is what a screen reader should hear. */
function Stars({ value }: { value: number }) {
  const { lang } = useRender()
  const n = Math.max(1, Math.min(5, Math.round(value || 5)))
  return (
    <span
      aria-label={lang === 'ar' ? `${n} من ٥` : `${n} out of 5`}
      style={{ color: 'var(--accent)', letterSpacing: '0.1em' }}
    >
      <span aria-hidden="true">{'★'.repeat(n) + '☆'.repeat(5 - n)}</span>
    </span>
  )
}

export function TestimonialsBlock({ block }: { block: Block }) {
  const p = block.props as unknown as Props
  const items = (p.items ?? []).filter((i) => hasText(i.quote))
  const single = block.variant === 'quote'

  // An empty testimonials block renders NOTHING on a published page. It is the one block that is
  // routinely added and left empty — the generator refuses to invent quotes (see its registry entry)
  // — and an empty "What our customers say" heading over nothing is worse than no section at all.
  if (items.length === 0) return null

  return (
    <section className="s-section">
      <div className={`s-wrap ${single ? 's-narrow s-center' : ''} s-stack-lg`}>
        {hasText(p.heading) && (
          <h2 className="s-h2 s-center">
            <SiteText value={p.heading} />
          </h2>
        )}
        <div className={single ? '' : 's-grid'}>
          {(single ? items.slice(0, 1) : items).map((item, i) => (
            <figure key={i} className={single ? '' : 's-card'} style={{ margin: 0 }}>
              <Stars value={item.rating} />
              <blockquote className={single ? 's-lead' : 's-body'} style={{ margin: '0.75rem 0 0' }}>
                <SiteProse value={item.quote} />
              </blockquote>
              {hasText(item.name) && (
                <figcaption className="s-small" style={{ marginBlockStart: '0.75rem', color: 'var(--muted)' }}>
                  — <SiteText value={item.name} />
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      </div>
    </section>
  )
}
